import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { SpectrogramPoint } from '@ledium/types';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AutocompleteBordered from '../AutocompleteBordered';
import AutocompleteOptionWithChart from '../AutocompleteOptionWithChart';

interface LightRecipeItem {
    id: string;
    name: string;
    description?: string;
    spectrogram?: SpectrogramPoint[];
}

type LightRecipeState = LightRecipeItem[] | LightRecipeItem;

interface SelectLightRecipeProps {
    onSelectLightRecipe?: (recipe: LightRecipeItem | null) => void;
    selectedLightRecipeId?: string;
}

export const SelectLightRecipe = ({ onSelectLightRecipe, selectedLightRecipeId }: SelectLightRecipeProps) => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const [lightRecipeList, setLightRecipeList] = React.useState<LightRecipeState>([]);
    const recipeId = selectedLightRecipeId || (formData.lightRecipe as LightRecipeItem | null)?.id || '';

    React.useEffect(() => {
        lediumApi.get<LightRecipeState>(`lightRecipes/${formData.readOnly ? recipeId : ''}`).then((data) => {
            setLightRecipeList(data ?? []);
        });
    }, [formData.readOnly, selectedLightRecipeId, formData.lightRecipe]);

    function getOptionLabel(option: LightRecipeItem) {
        return option.name + (option.description ? ` (${option.description})` : '');
    }

    function isOptionEqualToValue(option: LightRecipeItem, value: LightRecipeItem) {
        return option.id === value.id;
    }

    function onChange(_event: React.SyntheticEvent, lightRecipe: LightRecipeItem | null) {
        if (onSelectLightRecipe) {
            onSelectLightRecipe(lightRecipe);
        } else {
            pageActions.formData.updateFormItem({ lightRecipe });
        }
    }

    function renderInput(params: object) {
        return <TextField {...(params as React.ComponentProps<typeof TextField>)} label={i18n('select_light_recipe')} variant="outlined" />;
    }

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: LightRecipeItem) {
        return (
            <li {...props}>
                <AutocompleteOptionWithChart option={option} spectrogramData={option.spectrogram} size="small" />
            </li>
        );
    }

    if (formData.readOnly) {
        const recipe = lightRecipeList as LightRecipeItem;
        let recipeName: React.ReactNode = '';
        if (recipe?.name) {
            recipeName = recipe.name;
            if (recipe.description) {
                recipeName = `${recipeName} (${recipe.description})`;
            }
        } else {
            recipeName = i18n('no_light_recipe_selected');
        }
        return (
            <>
                <Typography variant="subtitle2">{i18n('light_recipe')}: </Typography>
                <span>{recipeId ? recipeName : i18n('no_light_recipe_selected')}</span>
            </>
        );
    }

    const recipeArray = lightRecipeList as LightRecipeItem[];
    return (
        <AutocompleteBordered
            value={recipeArray.find((i) => i.id === recipeId) || null}
            options={recipeArray}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={onChange}
            renderInput={renderInput}
            renderOption={renderOption}
        />
    );
};
